type CosmicImgixOptions = {
  w?: number;
  q?: number;
  dpr?: number;
};

/**
 * Deliver Cosmic media through Cosmic's own imgix-backed CDN with transform
 * params, instead of the raw `cdn.cosmicjs.com` original.
 *
 * The plain `cdn.cosmicjs.com/<file>` URL ignores query params and always
 * streams the full-size original against the Cosmic bandwidth quota. The
 * `imgix.cosmicjs.com/<file>?w=…&auto=format,compress` URL honors the params
 * and returns a resized/re-encoded image — typically 90%+ smaller — while
 * staying entirely within Cosmic (no third-party proxy, no extra vendor cost).
 *
 * Non-Cosmic URLs (e.g. Imagin Studio renders) are returned untouched.
 */
export const getCosmicImgixUrl = (
  url?: string,
  { w, q, dpr }: CosmicImgixOptions = {}
): string | undefined => {
  if (!url) return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  // Only Cosmic-hosted media supports these transforms.
  if (!/(?:^|\.)cosmicjs\.com$/i.test(parsed.hostname)) return url;

  // SVGs are vector (already tiny); imgix raster transforms would rasterize
  // them and lose scalability, so leave them untouched.
  if (/\.svg$/i.test(parsed.pathname)) return url;

  // The imgix host honors transform params; the cdn host ignores them.
  parsed.hostname = 'imgix.cosmicjs.com';

  parsed.searchParams.set('auto', 'format,compress');
  if (w) parsed.searchParams.set('w', String(w));
  if (q) parsed.searchParams.set('q', String(q));
  if (dpr) parsed.searchParams.set('dpr', String(dpr));

  return parsed.toString();
};

// Rich-text content renders inside containers capped at ~726px
// (`md:max-w-[726px]`), so 726 CSS px at 2x device pixel ratio covers every
// rendered size without shipping the multi-MB original.
const RICH_TEXT_IMG_WIDTH = 726;
const RICH_TEXT_IMG_DPR = 2;

// Matches an <img> tag. Deliberately simple: a `>` inside a quoted attribute
// value ends the match early, so such a tag is skipped rather than rewritten.
// That is miss-only (never corrupting), and Froala does not emit `>` inside
// attributes.
const IMG_TAG_REGEX = /<img\b[^>]*>/gi;
// Both `src` and lazy-loading `data-src` point at bytes the browser fetches, so
// rewrite either (and both — the `g` flag matters when a tag carries a
// `data-src` placeholder *and* a real `src`). The leading `\s` anchors the match
// to a real attribute boundary: `\b` would also match mid-name and rewrite
// `data-original-src` (a full-size URL a lightbox may rely on) or `:src`.
// `srcset` is not matched: `src` is not followed by `=` there.
const SRC_ATTR_REGEX = /(\s(?:data-)?src\s*=\s*)(?:"([^"]*)"|'([^']*)')/gi;

/**
 * Rewrite Cosmic-hosted <img> tags embedded in CMS rich text so they are
 * served resized through Cosmic's imgix CDN instead of as raw originals.
 *
 * Editors paste images into Froala rich-text fields, which stores raw
 * `<img ... src="https://cdn.cosmicjs.com/...">` (or param-less imgix) URLs —
 * each one streams the full-size multi-MB original on every page view.
 * Rewriting the src at render time fixes every existing content entry without
 * touching CMS data. Attribute order doesn't matter (Froala emits
 * alt/class/style before src).
 *
 * URLs that already carry a `w`/`h` transform, SVGs, and non-Cosmic hosts
 * (incl. Cloudinary-proxied `res.cloudinary.com/.../https://cdn.cosmicjs...`
 * URLs) are left untouched, so the rewrite is idempotent and safe to apply
 * at multiple stages of the same render pipeline.
 */
export const rewriteCosmicImagesInHtml = (html: string): string => {
  if (!html?.includes('cosmicjs.com')) return html;

  return html.replace(IMG_TAG_REGEX, (imgTag) =>
    imgTag.replace(
      SRC_ATTR_REGEX,
      (srcAttr, prefix, doubleQuoted, singleQuoted) => {
        const quote = doubleQuoted === undefined ? "'" : '"';
        // Rich text may HTML-encode ampersands inside attribute values.
        const url = (doubleQuoted ?? singleQuoted).replaceAll('&amp;', '&');

        let parsed: URL;
        try {
          parsed = new URL(url);
        } catch {
          return srcAttr;
        }

        if (!/^(cdn|imgix)\.cosmicjs\.com$/i.test(parsed.hostname)) {
          return srcAttr;
        }

        // Already sized — keep whatever transform the author chose.
        if (parsed.searchParams.has('w') || parsed.searchParams.has('h')) {
          return srcAttr;
        }

        const rewritten = getCosmicImgixUrl(url, {
          w: RICH_TEXT_IMG_WIDTH,
          dpr: RICH_TEXT_IMG_DPR,
        });
        if (!rewritten || rewritten === url) return srcAttr;

        // Re-encode `&` as `&amp;` — this string is injected as raw markup
        // (dangerouslySetInnerHTML / html-react-parser), and both decode the
        // entity back to `&` when reading the attribute.
        return `${prefix}${quote}${rewritten.replaceAll('&', '&amp;')}${quote}`;
      }
    )
  );
};
